// apps/provider/src/services/api/rehabPrograms.js
// Rehab program CRUD, phase management, prescription linking, patient exercise
// assignments, and composite operations (create from prescription, get complete program).
//
// Methods: createRehabProgram, getRehabPrograms, getRehabProgram, getRehabProgramPublic,
//          updateRehabProgram, deleteRehabProgram,
//          createRehabProgramPhase, getRehabProgramPhases, getRehabProgramPhasesPublic,
//          updateRehabProgramPhase, deleteRehabProgramPhase,
//          createRehabProgramPrescription, getRehabProgramPrescriptions, getRehabProgramPrescriptionsPublic,
//          updateRehabProgramPrescription, deleteRehabProgramPrescription,
//          createPatientExerciseAssignment, getPatientExerciseAssignments, getPatientExerciseAssignment,
//          updatePatientExerciseAssignment, deletePatientExerciseAssignment,
//          createRehabProgramFromPrescription, getCompleteRehabProgram
//
// Cross-domain calls (via composed `this`):
//   createRehabProgramFromPrescription → exercises.createExercisePrescription
//   getCompleteRehabProgram → this.getRehabProgram, this.getRehabProgramPhases, this.getRehabProgramPrescriptions
//
// Response types: @prism-ehr/shared → RehabProgram, RehabProgramPhase, RehabProgramPrescription,
//                 PatientExerciseAssignment, CompleteRehabProgram, RehabProgramPhaseWithPrescriptions

/**
 * @returns {Object} Rehab program domain methods (bound to `this` of the composed apiService)
 */
export function createRehabProgramsMethods() {
  return {

    // ============ Rehab Program CRUD ============

    /**
     * Create a new rehab program.
     *
     * Xano: POST /rehab_program
     * Xano staging file: 702_rehab_program_POST.xs
     *
     * @param {Object} data - Program data (name, description, condition, total_phases, expected_duration_weeks, created_by, organization_id, is_template, template_visibility)
     * @returns {Promise<import('@prism-ehr/shared').RehabProgram>}
     */
    async createRehabProgram(data) {
      return this.request('/rehab_program', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get rehab programs with optional filters.
     *
     * Xano: GET /rehab_program
     * Xano staging file: 701_rehab_program_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.created_by]
     * @param {boolean} [filters.is_template]
     * @param {string} [filters.template_visibility] - 'private' | 'organization' | 'public'
     * @param {string} [filters.condition]
     * @param {boolean} [filters.templates_page] - When true, returns template-specific filtering
     * @returns {Promise<import('@prism-ehr/shared').RehabProgram[]>}
     */
    async getRehabPrograms(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.created_by) queryParams.append('created_by', filters.created_by);
      if (filters.is_template !== undefined) queryParams.append('is_template', filters.is_template);
      if (filters.template_visibility) queryParams.append('template_visibility', filters.template_visibility);
      if (filters.condition) queryParams.append('condition', filters.condition);
      if (filters.templates_page !== undefined) queryParams.append('templates_page', filters.templates_page);

      return this.request(`/rehab_program?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single rehab program by ID.
     *
     * Xano: GET /rehab_program/{rehab_program_id}
     * Xano staging file: 700_rehab_program_rehab_program_id_GET.xs
     *
     * @param {number|string} id - Rehab program ID
     * @returns {Promise<import('@prism-ehr/shared').RehabProgram>}
     */
    async getRehabProgram(id) {
      return this.request(`/rehab_program/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single rehab program by ID (public, unauthenticated).
     *
     * Xano: GET /public/rehab_program/{rehab_program_id}
     * Xano staging file: 919_public_rehab_program_rehab_program_id_GET.xs
     *
     * @param {number|string} id - Rehab program ID
     * @returns {Promise<import('@prism-ehr/shared').RehabProgram>}
     */
    async getRehabProgramPublic(id) {
      return this.publicRequest(`/public/rehab_program/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Update an existing rehab program.
     *
     * Xano: PATCH /rehab_program/{rehab_program_id}
     * Xano staging file: 703_rehab_program_rehab_program_id_PATCH.xs
     *
     * @param {number|string} id - Rehab program ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').RehabProgram>}
     */
    async updateRehabProgram(id, data) {
      return this.request(`/rehab_program/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a rehab program.
     *
     * Xano: DELETE /rehab_program/{rehab_program_id}
     * Xano staging file: 699_rehab_program_rehab_program_id_DELETE.xs
     *
     * @param {number|string} id - Rehab program ID
     * @returns {Promise<void>}
     */
    async deleteRehabProgram(id) {
      return this.request(`/rehab_program/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Rehab Program Phase CRUD ============

    /**
     * Create a new phase within a rehab program.
     *
     * Xano: POST /rehab_program_phase
     * Xano staging file: 707_rehab_program_phase_POST.xs
     *
     * @param {Object} data - Phase data (rehab_program_id, phase_number, phase_name, duration_weeks, goals, criteria_for_progression)
     * @returns {Promise<import('@prism-ehr/shared').RehabProgramPhase>}
     */
    async createRehabProgramPhase(data) {
      return this.request('/rehab_program_phase', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get all phases for a rehab program.
     *
     * Xano: GET /rehab_program_phase
     * Xano staging file: 706_rehab_program_phase_GET.xs
     *
     * @param {number|string} rehabProgramId - Rehab program ID
     * @returns {Promise<import('@prism-ehr/shared').RehabProgramPhase[]>}
     */
    async getRehabProgramPhases(rehabProgramId) {
      return this.request(`/rehab_program_phase?rehab_program_id=${rehabProgramId}`, {
        method: 'GET'
      });
    },

    /**
     * Get all phases for a rehab program (public, unauthenticated).
     *
     * Xano: GET /public/rehab_program_phase
     * Xano staging file: 920_public_rehab_program_phase_GET.xs
     *
     * @param {number|string} rehabProgramId - Rehab program ID
     * @returns {Promise<import('@prism-ehr/shared').RehabProgramPhase[]>}
     */
    async getRehabProgramPhasesPublic(rehabProgramId) {
      return this.publicRequest(`/public/rehab_program_phase?rehab_program_id=${rehabProgramId}`, {
        method: 'GET'
      });
    },

    /**
     * Update a rehab program phase.
     *
     * Xano: PATCH /rehab_program_phase/{rehab_program_phase_id}
     * Xano staging file: 708_rehab_program_phase_rehab_program_phase_id_PATCH.xs
     *
     * @param {number|string} id - Phase ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').RehabProgramPhase>}
     */
    async updateRehabProgramPhase(id, data) {
      return this.request(`/rehab_program_phase/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a rehab program phase.
     *
     * Xano: DELETE /rehab_program_phase/{rehab_program_phase_id}
     * Xano staging file: 704_rehab_program_phase_rehab_program_phase_id_DELETE.xs
     *
     * @param {number|string} id - Phase ID
     * @returns {Promise<void>}
     */
    async deleteRehabProgramPhase(id) {
      return this.request(`/rehab_program_phase/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Rehab Program Prescription CRUD ============

    /**
     * Link an exercise prescription to a rehab program phase.
     *
     * Xano: POST /rehab_program_prescription
     * Xano staging file: 712_rehab_program_prescription_POST.xs
     *
     * @param {Object} data - Prescription link data (rehab_program_phase_id, exercise_prescription_id, frequency_per_week, sequence)
     * @returns {Promise<import('@prism-ehr/shared').RehabProgramPrescription>}
     */
    async createRehabProgramPrescription(data) {
      return this.request('/rehab_program_prescription', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get all prescriptions for a rehab program phase.
     * Returns prescriptions with exercise_prescription addon.
     *
     * Xano: GET /rehab_program_prescription
     * Xano staging file: 711_rehab_program_prescription_GET.xs
     *
     * @param {number|string} rehabProgramPhaseId - Phase ID
     * @returns {Promise<import('@prism-ehr/shared').RehabProgramPrescription[]>}
     */
    async getRehabProgramPrescriptions(rehabProgramPhaseId) {
      return this.request(`/rehab_program_prescription?rehab_program_phase_id=${rehabProgramPhaseId}`, {
        method: 'GET'
      });
    },

    /**
     * Get all prescriptions for a rehab program phase (public, unauthenticated).
     * Returns prescriptions with exercise_prescription addon.
     *
     * Xano: GET /public/rehab_program_prescription
     * Xano staging file: 921_public_rehab_program_prescription_GET.xs
     *
     * @param {number|string} rehabProgramPhaseId - Phase ID
     * @returns {Promise<import('@prism-ehr/shared').RehabProgramPrescription[]>}
     */
    async getRehabProgramPrescriptionsPublic(rehabProgramPhaseId) {
      return this.publicRequest(`/public/rehab_program_prescription?rehab_program_phase_id=${rehabProgramPhaseId}`, {
        method: 'GET'
      });
    },

    /**
     * Update a rehab program prescription.
     *
     * Xano: PATCH /rehab_program_prescription/{rehab_program_prescription_id}
     * Xano staging file: 713_rehab_program_prescription_rehab_program_prescription_id_PATCH.xs
     *
     * @param {number|string} id - Prescription link ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').RehabProgramPrescription>}
     */
    async updateRehabProgramPrescription(id, data) {
      return this.request(`/rehab_program_prescription/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a rehab program prescription.
     *
     * Xano: DELETE /rehab_program_prescription/{rehab_program_prescription_id}
     * Xano staging file: 709_rehab_program_prescription_rehab_program_prescription_id_DELETE.xs
     *
     * @param {number|string} id - Prescription link ID
     * @returns {Promise<void>}
     */
    async deleteRehabProgramPrescription(id) {
      return this.request(`/rehab_program_prescription/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Patient Exercise Assignment CRUD ============

    /**
     * Create a patient exercise assignment.
     * Sends a notification to the patient and optionally creates an RTM enrollment.
     *
     * Xano: POST /patient_exercise_assignment
     * Xano staging file: 717_patient_exercise_assignment_POST.xs
     *
     * Side effects:
     * - Sends `program_assigned` notification to the patient
     * - If `rtm_enabled` is true, creates an `rtm_enrollment` record
     *
     * @param {Object} data - Assignment data (patient_id, provider_id, assignment_type, workout_plan_id, rehab_program_id, exercise_prescription_id, start_date, end_date, status, notes, rtm_enabled)
     * @returns {Promise<import('@prism-ehr/shared').PatientExerciseAssignment>}
     */
    async createPatientExerciseAssignment(data) {
      return this.request('/patient_exercise_assignment', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Get patient exercise assignments with optional filters.
     * Returns assignments with patient and exercise_prescription addons.
     *
     * Xano: GET /patient_exercise_assignment
     * Xano staging file: 716_patient_exercise_assignment_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.provider_id]
     * @param {string} [filters.status] - e.g. 'active', 'completed', 'paused'
     * @param {string} [filters.assignment_type] - e.g. 'rehab_program', 'workout_plan', 'exercise_prescription'
     * @param {string} [filters.rehab_program_id]
     * @param {string} [filters.workout_plan_id]
     * @returns {Promise<import('@prism-ehr/shared').PatientExerciseAssignment[]>}
     */
    async getPatientExerciseAssignments(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.assignment_type) queryParams.append('assignment_type', filters.assignment_type);
      if (filters.rehab_program_id) queryParams.append('rehab_program_id', filters.rehab_program_id);
      if (filters.workout_plan_id) queryParams.append('workout_plan_id', filters.workout_plan_id);

      return this.request(`/patient_exercise_assignment?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single patient exercise assignment by ID.
     *
     * Xano: GET /patient_exercise_assignment/{patient_exercise_assignment_id}
     * Xano staging file: 715_patient_exercise_assignment_patient_exercise_assignment_id_GET.xs
     *
     * @param {number|string} id - Assignment ID
     * @returns {Promise<import('@prism-ehr/shared').PatientExerciseAssignment>}
     */
    async getPatientExerciseAssignment(id) {
      return this.request(`/patient_exercise_assignment/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Update a patient exercise assignment.
     *
     * Xano: PATCH /patient_exercise_assignment/{patient_exercise_assignment_id}
     * Xano staging file: 718_patient_exercise_assignment_patient_exercise_assignment_id_PATCH.xs
     *
     * @param {number|string} id - Assignment ID
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').PatientExerciseAssignment>}
     */
    async updatePatientExerciseAssignment(id, data) {
      return this.request(`/patient_exercise_assignment/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a patient exercise assignment.
     *
     * Xano: DELETE /patient_exercise_assignment/{patient_exercise_assignment_id}
     * Xano staging file: 714_patient_exercise_assignment_patient_exercise_assignment_id_DELETE.xs
     *
     * @param {number|string} id - Assignment ID
     * @returns {Promise<void>}
     */
    async deletePatientExerciseAssignment(id) {
      return this.request(`/patient_exercise_assignment/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Composite / Helper Methods ============

    /**
     * Create a rehab program from a single exercise prescription.
     * Creates the prescription, program, phase, and links them together.
     *
     * Cross-domain: calls exercises.createExercisePrescription via composed `this`.
     *
     * @param {Object} prescriptionData - Exercise prescription data (provider_id, organization_id, description, schedule)
     * @param {Object} programData - Program metadata (name, description, condition, duration_weeks, phase_name, goals, criteria_for_progression, is_template, template_visibility)
     * @returns {Promise<{program: import('@prism-ehr/shared').RehabProgram, phase: import('@prism-ehr/shared').RehabProgramPhase, prescription: import('@prism-ehr/shared').ExercisePrescription}>}
     */
    async createRehabProgramFromPrescription(prescriptionData, programData) {
      try {
        // 1. Create the exercise prescription
        const prescription = await this.createExercisePrescription(prescriptionData);

        // 2. Create the rehab program
        const program = await this.createRehabProgram({
          name: programData.name,
          description: programData.description || prescriptionData.description,
          condition: programData.condition,
          total_phases: 1,
          expected_duration_weeks: programData.duration_weeks || 4,
          created_by: prescriptionData.provider_id,
          organization_id: prescriptionData.organization_id,
          is_template: programData.is_template || false,
          template_visibility: programData.template_visibility || 'private'
        });

        // 3. Create the phase
        const phase = await this.createRehabProgramPhase({
          rehab_program_id: program.id,
          phase_number: 1,
          phase_name: programData.phase_name || 'Phase 1',
          duration_weeks: programData.duration_weeks || 4,
          goals: programData.goals || [],
          criteria_for_progression: programData.criteria_for_progression || ''
        });

        // 4. Link prescription to phase
        await this.createRehabProgramPrescription({
          rehab_program_phase_id: phase.id,
          exercise_prescription_id: prescription.id,
          frequency_per_week: prescriptionData.schedule?.rehab_frequency?.times_per_week || 3,
          sequence: 1
        });

        return {
          program,
          phase,
          prescription
        };
      } catch (error) {
        console.error('Failed to create rehab program from prescription:', error);
        throw error;
      }
    },

    /**
     * Get a complete rehab program with nested phases and prescriptions.
     *
     * Note: Xano also provides GET /rehab_program/{id}/phases/prescriptions
     * (staging file: 788_rehab_program_rehab_program_id_phases_prescriptions_GET.xs)
     * but this composite method fetches each level separately for full control.
     *
     * @param {number|string} rehabProgramId - Rehab program ID
     * @returns {Promise<import('@prism-ehr/shared').CompleteRehabProgram>}
     */
    async getCompleteRehabProgram(rehabProgramId) {
      try {
        const program = await this.getRehabProgram(rehabProgramId);
        const phases = await this.getRehabProgramPhases(rehabProgramId);

        // Get prescriptions for each phase
        const phasesWithPrescriptions = await Promise.all(
          phases.map(async (phase) => {
            const prescriptions = await this.getRehabProgramPrescriptions(phase.id);
            return { ...phase, prescriptions };
          })
        );

        return {
          ...program,
          phases: phasesWithPrescriptions
        };
      } catch (error) {
        console.error('Failed to get complete rehab program:', error);
        throw error;
      }
    },

  };
}
